import React from "react";
import { label, fmtScore, scorePct, SKILLS } from "../labels";

const Empty = ({ children }) => <p className="text-[13px] text-[#132C44]/60 py-4" data-testid="eid-sv-empty">{children}</p>;

const Card = ({ children, testId }) => (
  <div className="p-4 rounded-2xl bg-white border border-[#132C44]/10" data-testid={testId}>
    {children}
  </div>
);

const Pill = ({ children, tone = "sage" }) => {
  const tones = {
    sage: "bg-[#8AA986]/20 text-[#4F6B47]",
    navy: "bg-[#132C44]/10 text-[#132C44]",
    gold: "bg-[#B08D3C]/15 text-[#7d6224]",
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
};

const Bar = ({ pct }) => (
  <div className="h-2 rounded-full bg-[#132C44]/10 overflow-hidden">
    <div className="h-full bg-[#8AA986] transition-[width] duration-500" style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />
  </div>
);

export function EvaluationsSection({ assessments }) {
  if (!assessments.length) return <Empty>Todavía no hay evaluaciones publicadas.</Empty>;
  return (
    <div className="space-y-3" data-testid="eid-sv-evaluations">
      {assessments.map((a) => (
        <Card key={a.id} testId={`eid-sv-eval-${a.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-1.5 flex-wrap">
              <Pill>{label(a.skill)}</Pill>
              {a.activityType && <Pill tone="navy">{label(a.activityType)}</Pill>}
            </div>
            <span className="text-[11px] text-[#132C44]/60">{a.date}</span>
          </div>
          <h4 className="mt-2 font-semibold text-[14px]">{a.criterion}</h4>
          <div className="mt-1 flex gap-3 text-[13px] flex-wrap">
            {fmtScore(a) && <span className="font-semibold">Nota: {fmtScore(a)}</span>}
            {(a.qualitative || a.rubricLevel) && (
              <span className="text-[#4F6B47] font-medium">{a.qualitative || label(a.rubricLevel)}</span>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

export function GradesSection({ assessments }) {
  const graded = assessments.filter((a) => fmtScore(a));
  if (!graded.length) return <Empty>Todavía no hay calificaciones numéricas publicadas.</Empty>;
  return (
    <div className="overflow-x-auto rounded-2xl bg-white border border-[#132C44]/10">
      <table className="w-full text-[13px]" data-testid="eid-sv-grades">
        <thead className="bg-[#132C44]/5 text-left text-[10px] uppercase tracking-widest">
          <tr>
            <th className="p-3">Fecha</th>
            <th className="p-3">Habilidad</th>
            <th className="p-3">Actividad</th>
            <th className="p-3 text-right">Nota</th>
          </tr>
        </thead>
        <tbody>
          {graded.map((a) => (
            <tr key={a.id} className="border-t border-[#132C44]/5">
              <td className="p-3 whitespace-nowrap">{a.date}</td>
              <td className="p-3">{label(a.skill)}</td>
              <td className="p-3">{label(a.activityType)}</td>
              <td className="p-3 text-right font-semibold">{fmtScore(a)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function skillStats(assessments, skill) {
  const rows = assessments.filter((a) => a.skill === skill && scorePct(a) !== null).sort((x, y) => (x.date < y.date ? -1 : 1));
  if (!rows.length) return null;
  const pcts = rows.map(scorePct);
  return {
    count: rows.length,
    avg: pcts.reduce((s, p) => s + p, 0) / pcts.length,
    first: pcts[0],
    last: pcts[pcts.length - 1],
    lastRow: rows[rows.length - 1],
  };
}

export function ProgressSection({ assessments, goals, attendance }) {
  const met = goals.filter((g) => g.status === "met").length;
  const active = goals.filter((g) => g.status === "active").length;
  const present = attendance.filter((a) => a.status === "present" || a.status === "late").length;
  const attPct = attendance.length ? Math.round((present / attendance.length) * 100) : null;
  return (
    <div className="space-y-6" data-testid="eid-sv-progress">
      <div className="grid grid-cols-3 gap-3">
        <Card><div className="text-[10px] uppercase tracking-widest text-[#132C44]/60">Objetivos alcanzados</div><div className="font-serif text-2xl font-bold mt-1" data-testid="eid-sv-goals-met">{met}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-[#132C44]/60">En desarrollo</div><div className="font-serif text-2xl font-bold mt-1">{active}</div></Card>
        <Card><div className="text-[10px] uppercase tracking-widest text-[#132C44]/60">Asistencia</div><div className="font-serif text-2xl font-bold mt-1" data-testid="eid-sv-att-pct">{attPct === null ? "—" : `${attPct}%`}</div></Card>
      </div>
      <div className="space-y-4">
        <h3 className="font-serif text-xl font-bold">Evolución por habilidad</h3>
        {SKILLS.map((s) => {
          const st = skillStats(assessments, s);
          const trend = st && st.count > 1 ? st.last - st.first : 0;
          return (
            <div key={s} data-testid={`eid-sv-skill-${s}`}>
              <div className="flex items-center justify-between mb-1 text-[13px]">
                <span className="font-semibold">{label(s)}</span>
                <span className="text-[12px] text-[#132C44]/70">
                  {st
                    ? `Última: ${fmtScore(st.lastRow)} · ${st.count} evaluación${st.count > 1 ? "es" : ""}${trend > 0 ? " · en mejora" : trend < 0 ? " · a reforzar" : ""}`
                    : "Sin notas aún"}
                </span>
              </div>
              <Bar pct={st?.avg} />
            </div>
          );
        })}
        <p className="text-[11px] text-[#132C44]/55">La barra muestra el promedio de las notas publicadas, en relación con la escala de cada evaluación.</p>
      </div>
    </div>
  );
}

export function AttendanceSection({ attendance }) {
  if (!attendance.length) return <Empty>Sin registros de asistencia.</Empty>;
  const tone = { present: "bg-[#8AA986]/25 text-[#4F6B47]", late: "bg-[#E8B84A]/30 text-[#8a6a1a]", justified: "bg-[#5B84A6]/25 text-[#2d4b6a]", absent: "bg-[#9B2C3B]/15 text-[#7a222f]" };
  const counts = attendance.reduce((m, a) => ({ ...m, [a.status]: (m[a.status] || 0) + 1 }), {});
  return (
    <div className="space-y-4" data-testid="eid-sv-attendance">
      <div className="flex flex-wrap gap-2 text-[12px]">
        {Object.entries(counts).map(([k, v]) => (
          <span key={k} className="px-3 py-1 rounded-full bg-white border border-[#132C44]/10">{label(k)}: <strong>{v}</strong></span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {attendance.map((a) => (
          <span key={a.id} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${tone[a.status]}`}>
            {a.date} · {label(a.status)}
          </span>
        ))}
      </div>
    </div>
  );
}

export function GoalsSection({ goals }) {
  const groups = [
    ["active", "En desarrollo"],
    ["met", "Alcanzados"],
  ];
  if (!goals.length) return <Empty>Todavía no hay objetivos asignados.</Empty>;
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="eid-sv-goals">
      {groups.map(([k, title]) => (
        <div key={k}>
          <h3 className="font-semibold text-[14px] mb-3">{title}</h3>
          <div className="space-y-3">
            {goals.filter((g) => g.status === k).map((g) => (
              <Card key={g.id}>
                <h4 className="font-semibold text-[14px]">{g.title}</h4>
                {g.description && <p className="text-[12px] text-[#132C44]/70 mt-1">{g.description}</p>}
              </Card>
            ))}
            {!goals.some((g) => g.status === k) && <Empty>Ninguno por ahora.</Empty>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FeedbackSection({ assessments }) {
  const items = assessments.filter((a) => a.comment || a.achievements || a.toImprove);
  if (!items.length) return <Empty>Todavía no hay devoluciones docentes publicadas.</Empty>;
  return (
    <div className="space-y-3" data-testid="eid-sv-feedback">
      {items.map((a) => (
        <Card key={a.id}>
          <div className="flex items-center justify-between text-[11px] text-[#132C44]/60">
            <span className="uppercase tracking-widest font-semibold">{label(a.skill)} · {a.criterion}</span>
            <span>{a.date}</span>
          </div>
          {a.comment && <p className="mt-2 text-[13px]">{a.comment}</p>}
          {a.achievements && <p className="mt-2 text-[12px]"><strong className="text-[#4F6B47]">Logros:</strong> {a.achievements}</p>}
          {a.toImprove && <p className="mt-1 text-[12px]"><strong className="text-[#7d6224]">A reforzar:</strong> {a.toImprove}</p>}
          {a.teacherName && <p className="mt-2 text-[11px] text-[#132C44]/55">— {a.teacherName}</p>}
        </Card>
      ))}
    </div>
  );
}
